import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  adminListTopicSeeds,
  adminCreateTopicSeed,
  adminDeleteTopicSeed,
} from "@/lib/admin.functions";
import { formatDate } from "@/lib/site";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/topics")({
  component: TopicsPage,
});

const CATEGORIES = [
  "google-business-profile",
  "local-rankings",
  "reviews-reputation",
  "google-maps",
  "ai-search",
  "industry-news",
];

function TopicsPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin", "topic-seeds"],
    queryFn: () => adminListTopicSeeds(),
  });

  const [title, setTitle] = useState("");
  const [angle, setAngle] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [categorySlug, setCategorySlug] = useState("");

  const create = useMutation({
    mutationFn: () =>
      adminCreateTopicSeed({
        data: {
          title,
          angle: angle || null,
          source_url: sourceUrl || null,
          category_slug: categorySlug || null,
        },
      }),
    onSuccess: () => {
      toast.success("Topic seed added");
      setTitle("");
      setAngle("");
      setSourceUrl("");
      setCategorySlug("");
      qc.invalidateQueries({ queryKey: ["admin", "topic-seeds"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Failed to add topic"),
  });

  const del = useMutation({
    mutationFn: (id: string) => adminDeleteTopicSeed({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "topic-seeds"] });
    },
  });

  return (
    <div className="grid gap-10 lg:grid-cols-[1fr_1.3fr]">
      <section>
        <h1 className="headline-display text-3xl">Topic seeds</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Optional. Seeds are forced into the next <Link to="/admin/ideas" search={{ status: "new" }} className="underline">discovery run</Link> before
          web search — use this when you want to guarantee an article on a specific story.
          Discovery runs daily, or click <em>Run discovery now</em> on the Ideas page.
        </p>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (title.length < 3) {
              toast.error("Title is too short");
              return;
            }
            create.mutate();
          }}
          className="mt-6 space-y-3 border border-border p-5 bg-paper-soft"
        >
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="mt-1 w-full border border-border bg-background px-3 py-2"
              placeholder="What changed in the Maps spam policy this month?"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Angle (optional)</label>
            <textarea
              value={angle}
              onChange={(e) => setAngle(e.target.value)}
              rows={3}
              className="mt-1 w-full border border-border bg-background px-3 py-2"
              placeholder="Focus on multi-location brands and what their listings managers should do this week."
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Source URL (optional)</label>
            <input
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              className="mt-1 w-full border border-border bg-background px-3 py-2"
              placeholder="https://…"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Category (optional)</label>
            <select
              value={categorySlug}
              onChange={(e) => setCategorySlug(e.target.value)}
              className="mt-1 w-full border border-border bg-background px-3 py-2"
            >
              <option value="">— let the model decide —</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={create.isPending}
            className="bg-foreground text-background px-5 py-2 font-medium hover:bg-ochre-deep transition-colors disabled:opacity-50"
          >
            {create.isPending ? "Saving…" : "Add seed"}
          </button>
        </form>
      </section>

      <section>
        <h2 className="headline-display text-2xl">Queue</h2>
        <div className="mt-4 divide-y divide-border border border-border">
          {isLoading && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
          {!isLoading && (!data || data.length === 0) && (
            <div className="p-6 text-sm text-muted-foreground">No seeds yet.</div>
          )}
          {data?.map((s: any) => (
            <div key={s.id} className="p-4 text-sm">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium">{s.title}</div>
                  {s.angle && <div className="text-muted-foreground mt-1">{s.angle}</div>}
                  <div className="mt-2 text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
                    {s.category_slug && <span>{s.category_slug}</span>}
                    {s.source_url && (
                      <a href={s.source_url} target="_blank" rel="noreferrer" className="underline">
                        source
                      </a>
                    )}
                    <span>added {formatDate(s.created_at)}</span>
                    {s.used_at && <span className="text-green-700">used {formatDate(s.used_at)}</span>}
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (confirm("Delete this seed?")) del.mutate(s.id);
                  }}
                  className="text-xs text-red-700 underline"
                >
                  delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
